import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, Users, Trophy, Calendar, MapPin, User, Upload } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Navbar from '@/components/layout/Navbar';
import BulkUploadModal from '@/components/BulkUploadModal';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO } from 'date-fns';

export default function TeamDetailPage({ user }) {
  const { teamId } = useParams();
  const [team, setTeam] = useState(null);
  const [players, setPlayers] = useState([]);
  const [matches, setMatches] = useState([]);
  const [league, setLeague] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchPlayers = async () => {
    try {
      const playersRes = await axios.get(`${API}/players`, { params: { team_id: teamId } });
      setPlayers(playersRes.data);
    } catch (error) {
      console.error('Error fetching players:', error);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const teamRes = await axios.get(`${API}/teams/${teamId}`);
        setTeam(teamRes.data);
        
        const [playersRes, matchesRes, leagueRes] = await Promise.all([
          axios.get(`${API}/players`, { params: { team_id: teamId } }),
          axios.get(`${API}/matches`, { params: { team_id: teamId } }),
          axios.get(`${API}/leagues/${teamRes.data.league_id}`)
        ]);
        
        setPlayers(playersRes.data);
        setMatches(matchesRes.data);
        setLeague(leagueRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [teamId]);

  // Check if user is admin
  const isAdmin = user?.role === 'admin';

  const formatDateTime = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'MMM d, yyyy • h:mm a');
    } catch {
      return dateStr;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'Batsman': return 'bg-blue-100 text-blue-700';
      case 'Bowler': return 'bg-green-100 text-green-700';
      case 'All-rounder': return 'bg-purple-100 text-purple-700';
      case 'Wicketkeeper': return 'bg-orange-100 text-orange-700';
      default: return 'bg-slate-100 text-slate-700';
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} setUser={() => {}} />
        <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto">
            <div className="skeleton h-12 w-64 rounded mb-4"></div>
            <div className="skeleton h-6 w-32 rounded mb-8"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!team) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar user={user} setUser={() => {}} />
        <div className="pt-24 pb-12 px-4 sm:px-6 lg:px-8">
          <div className="max-w-7xl mx-auto text-center">
            <h1 className="text-2xl font-bold text-slate-900">Team not found</h1>
            <Link to="/leagues" className="text-blue-600 hover:underline mt-4 inline-block">
              Back to Leagues
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar user={user} setUser={() => {}} />
      
      {/* Header */}
      <div className="pt-24 pb-8 px-4 sm:px-6 lg:px-8 bg-blue-600">
        <div className="max-w-7xl mx-auto">
          {league && (
            <Link 
              to={`/leagues/${league.league_id}`} 
              className="inline-flex items-center gap-2 text-white/70 hover:text-white mb-4 text-sm"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to {league.name}
            </Link>
          )}
          
          <div className="flex flex-col sm:flex-row items-start gap-6">
            {team.logo_url ? (
              <img src={team.logo_url} alt={team.name} className="w-24 h-24 rounded-2xl object-cover" />
            ) : (
              <div className="w-24 h-24 rounded-2xl team-logo-placeholder text-4xl">
                {team.short_name?.charAt(0) || team.name.charAt(0)}
              </div>
            )}
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-bold text-white">{team.name}</h1>
              {team.short_name && (
                <Badge className="mt-2 bg-white/20 text-white">{team.short_name}</Badge>
              )}
              {team.captain_name && (
                <p className="mt-3 text-white/70 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Captain: {team.captain_name}
                </p>
              )}
              {league && (
                <Link 
                  to={`/leagues/${league.league_id}`}
                  className="mt-2 text-white/70 flex items-center gap-2 hover:text-white text-sm"
                >
                  <Trophy className="w-4 h-4" />
                  {league.name} • {league.season}
                </Link>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="px-4 sm:px-6 lg:px-8 -mt-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-3 gap-4">
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 text-center">
                <div className="font-stats text-2xl sm:text-3xl text-blue-600">{players.length}</div>
                <div className="text-xs sm:text-sm text-slate-500">Players</div>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 text-center">
                <div className="font-stats text-2xl sm:text-3xl text-blue-600">{matches.length}</div>
                <div className="text-xs sm:text-sm text-slate-500">Matches</div>
              </CardContent>
            </Card>
            <Card className="glass-card rounded-2xl">
              <CardContent className="p-4 text-center">
                <div className="font-stats text-2xl sm:text-3xl text-blue-600">
                  {matches.filter(m => m.status === 'completed').length}
                </div>
                <div className="text-xs sm:text-sm text-slate-500">Played</div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <Tabs defaultValue="roster" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-6 rounded-xl bg-slate-100 p-1">
              <TabsTrigger value="roster" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Users className="w-4 h-4 mr-2" />
                Roster ({players.length})
              </TabsTrigger>
              <TabsTrigger value="matches" className="rounded-lg data-[state=active]:bg-white data-[state=active]:shadow-sm">
                <Calendar className="w-4 h-4 mr-2" />
                Matches ({matches.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent value="roster">
              {/* Bulk Upload Button - Admin Only */}
              {isAdmin && (
                <div className="flex justify-end mb-4">
                  <BulkUploadModal 
                    teamId={teamId} 
                    teamName={team?.name} 
                    onUploadComplete={fetchPlayers}
                  />
                </div>
              )}

              {players.length > 0 ? (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {players.map((player) => (
                    <Card key={player.player_id} className="glass-card rounded-2xl">
                      <CardContent className="p-4">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 rounded-xl bg-slate-200 flex items-center justify-center text-slate-500 font-semibold">
                            {player.name.charAt(0)}
                          </div>
                          <div className="flex-1 min-w-0">
                            <h3 className="font-semibold text-slate-900 truncate">{player.name}</h3>
                            <Badge className={`mt-1 ${getRoleBadgeColor(player.player_role)}`}>
                              {player.player_role}
                            </Badge>
                            <div className="mt-2 text-xs text-slate-500 space-y-0.5">
                              <p>Batting: {player.batting_style}</p>
                              <p>Bowling: {player.bowling_style}</p>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              ) : (
                <div className="empty-state glass-card rounded-2xl">
                  <div className="empty-state-icon">
                    <Users className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">No players yet</h3>
                  <p className="text-slate-500 mt-2">Players will appear here once added</p>
                </div>
              )}
            </TabsContent>

            <TabsContent value="matches">
              {matches.length > 0 ? (
                <div className="space-y-4">
                  {matches.map((match) => {
                    const isTeamA = match.team_a_id === teamId;
                    const opponentId = isTeamA ? match.team_b_id : match.team_a_id;
                    
                    return (
                      <Link key={match.match_id} to={`/matches/${match.match_id}`}>
                        <Card className="match-card glass-card glass-card-hover rounded-2xl cursor-pointer">
                          <CardContent className="p-4">
                            <div className="flex items-center justify-between">
                              <div className="flex items-center gap-4">
                                <Badge className={
                                  match.status === 'live' ? 'badge-live' :
                                  match.status === 'scheduled' ? 'badge-scheduled' :
                                  'badge-completed'
                                }>
                                  {match.status.toUpperCase()}
                                </Badge>
                                <div>
                                  <p className="font-semibold text-slate-900">
                                    vs {isTeamA ? 'Team B' : 'Team A'}
                                  </p>
                                  <p className="text-sm text-slate-500">{formatDateTime(match.match_date_time)}</p>
                                </div>
                              </div>
                              <div className="text-right text-sm text-slate-500">
                                <div className="flex items-center gap-1">
                                  <MapPin className="w-4 h-4" />
                                  {match.ground}
                                </div>
                                <p>{match.overs} overs</p>
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <div className="empty-state glass-card rounded-2xl">
                  <div className="empty-state-icon">
                    <Calendar className="w-8 h-8 text-slate-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900">No matches yet</h3>
                  <p className="text-slate-500 mt-2">Matches will appear here once scheduled</p>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
